(() => {
  if (customElements.get('mvp-announcement')) return;
  class MvpAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.viewport = this.querySelector('[data-ticker-viewport]');
      this.track = this.querySelector('[data-ticker-track]');
      this.source = this.querySelector('[data-ticker-source]');
      if (!this.source || !this.viewport || !this.track) return;
      this.originals = [...this.source.children].filter(el => el.matches('[data-ticker-item]'));
      this.pauseButton = this.querySelector('[data-ticker-pause]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.hovered = this.focused = this.selected = this.userPaused = false;
      this.inView = true;
      this.hidden = this.originals.length === 0;
      this.onMotion = () => { this.signature = ''; this.schedule(); };
      this.reducedMotion.addEventListener('change', this.onMotion);
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(this.viewport);
      this.originals.forEach(el => this.resize.observe(el));
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.updatePlayback();
      });
      this.visibility.observe(this);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.updatePlayback(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.updatePlayback(); }, { signal });
      this.addEventListener('focusin', event => {
        if (!this.viewport.contains(event.target)) return;
        this.focused = true;
        this.reveal(event.target.closest('[data-ticker-item]'));
        this.updatePlayback();
      }, { signal });
      this.addEventListener('focusout', event => {
        if (this.viewport.contains(event.relatedTarget)) return;
        this.focused = false;
        this.updatePlayback();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
        this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Reanudar anuncios' : 'Pausar anuncios');
        this.pauseButton.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.userPaused);
        this.pauseButton.querySelector('[data-resume-icon]').toggleAttribute('hidden', !this.userPaused);
        this.updatePlayback();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const item = this.originals.find(el => el.dataset.blockId === event.detail?.blockId);
        if (!item) return;
        this.selected = true;
        this.reveal(item);
        this.updatePlayback();
      }, { signal });
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.originals.some(el => el.dataset.blockId === event.detail?.blockId)) return;
        this.selected = false;
        this.updatePlayback();
      }, { signal });
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(); });
      this.schedule();
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.build(); });
    }
    cleanCopies() {
      this.source.querySelectorAll('[data-ticker-repeat]').forEach(el => el.remove());
      this.track.querySelectorAll('[data-ticker-mirror]').forEach(el => el.remove());
    }
    copy(element) {
      const result = element.cloneNode(true);
      [result, ...result.querySelectorAll('*')].forEach(el => {
        [...el.attributes].forEach(attr => {
          if (attr.name === 'id' || attr.name === 'data-block-id' || attr.name.startsWith('data-shopify') || attr.name.startsWith('aria-labelledby') || attr.name.startsWith('aria-describedby')) el.removeAttribute(attr.name);
        });
        if (el.matches('a,button,input,select,textarea,[tabindex]')) el.setAttribute('tabindex', '-1');
      });
      result.querySelectorAll('style').forEach(el => el.remove());
      result.setAttribute('aria-hidden', 'true');
      return result;
    }
    build() {
      const isStatic = this.dataset.motion === 'false' || this.reducedMotion.matches;
      const width = this.viewport.clientWidth;
      if (!width || !this.originals.length) return;
      const sizes = this.originals.map(el => el.getBoundingClientRect().width.toFixed(2));
      const signature = [width, isStatic, this.dataset.velocity, this.dataset.direction, ...sizes].join(':');
      if (signature === this.signature) return;
      this.signature = signature;
      this.animation?.cancel();
      this.animation = null;
      this.cleanCopies();
      this.dataset.ready = 'true';
      this.dataset.static = String(isStatic);
      if (this.pauseButton) this.pauseButton.hidden = isStatic;
      if (isStatic) return;
      // Measure after leaving the static layout, so long messages retain their natural width.
      const baseWidth = this.source.getBoundingClientRect().width;
      if (baseWidth < 1) return;
      const repeats = Math.max(1, Math.ceil((width + 2) / baseWidth));
      for (let i = 1; i < repeats; i++) {
        this.originals.forEach(item => {
          const clone = this.copy(item);
          clone.dataset.tickerRepeat = '';
          this.source.append(clone);
        });
      }
      this.distance = this.source.getBoundingClientRect().width;
      const mirror = this.copy(this.source);
      mirror.removeAttribute('data-ticker-source');
      mirror.dataset.tickerMirror = '';
      this.track.append(mirror);
      const velocity = Math.min(160, Math.max(10, Number(this.dataset.velocity) || 45));
      this.duration = this.distance / velocity * 1000;
      const start = { transform: 'translate3d(0,0,0)' };
      const end = { transform: `translate3d(${-this.distance}px,0,0)` };
      this.animation = this.track.animate(this.dataset.direction === 'right' ? [end, start] : [start, end], {
        duration: this.duration, iterations: Infinity, easing: 'linear'
      });
      // The measured distance includes the final separator and gap: both groups join exactly.
      this.updatePlayback();
    }
    reveal(item) {
      if (!item || !this.animation || !this.distance) return;
      const offset = item.getBoundingClientRect().left - this.source.getBoundingClientRect().left;
      const fraction = Math.max(0, Math.min(.999999, offset / this.distance));
      this.animation.currentTime = (this.dataset.direction === 'right' ? 1 - fraction : fraction) * this.duration;
    }
    updatePlayback() {
      if (!this.animation) return;
      const paused = this.userPaused || this.focused || this.selected || document.hidden || !this.inView || (this.hovered && this.dataset.hoverPause === 'true');
      paused ? this.animation.pause() : this.animation.play();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.reducedMotion?.removeEventListener('change', this.onMotion);
      cancelAnimationFrame(this.frame);
      this.animation?.cancel();
      this.cleanCopies();
      this.signature = '';
      delete this.dataset.ready;
      delete this.dataset.static;
    }
  }
  customElements.define('mvp-announcement', MvpAnnouncement);
})();
